import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2, Wand2, X } from 'lucide-react';
import { openItemApi } from '@/api/openItem.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DocumentReferences } from '@/components/fin/DocumentReferences';
import { cn } from '@/utils/cn';
import { isCreditItem, type OpenItemDto } from '@/types/openItem.types';

/** What the voucher will send: which document, and how much of the tender goes to it. */
export interface VoucherAllocationPick {
  openItemId:      string;
  allocatedAmount: number;
}

/** An allocation as the saved voucher returns it — enough to show it without today's open items. */
export interface SavedVoucherAllocation {
  openItemId:      string;
  documentNumber:  string;
  documentDate?:   string | null;
  dueDate?:        string | null;
  allocatedAmount: number;
}

interface Props {
  partyType:    'Supplier' | 'Customer';
  /** Empty until a party is chosen — the picker explains itself rather than showing an empty grid. */
  partyId:       string;
  currencyCode:  string;
  /** What the voucher pays. The picks may total less; never more. */
  tenderTotal:   number;
  value:         VoucherAllocationPick[];
  onChange:      (picks: VoucherAllocationPick[]) => void;
  /** Posted vouchers show their settlement read-only. */
  readOnly?:     boolean;
  /**
   * Whether the caller may raise this voucher settling nothing (ML-07). The server is the control;
   * this only decides whether the operator is told now or refused on save.
   */
  canRaiseOnAccount?: boolean;
  /**
   * The settlement as this component understands it, reported so the form's Save guard does not
   * have to work it out again (ML-07).
   *
   * The form holds allocations as ids and amounts, which does not say which of them are advances —
   * and once an advance funds part of the settlement, "settles more than it pays" can no longer be
   * decided from the tender alone. Summing them all against the tender rejected exactly the
   * vouchers this requirement exists to allow.
   */
  onSummaryChange?: (summary: VoucherAllocationSummary) => void;
  /**
   * The voucher's allocations as saved (ADR-003). Read-only, they are what the Settles tab shows:
   * once posted, a fully settled document is no longer outstanding, so building the rows from the
   * outstanding list made the bills this voucher paid disappear on recall.
   */
  saved?: SavedVoucherAllocation[];
}

export interface VoucherAllocationSummary {
  /** What the picked bills come to. */
  settling:        number;
  /** What the picked advances contribute. */
  advanceApplied:  number;
  /** Settling more than the tender and the advances together cover. */
  overApplied:     boolean;
  /** Applying more advance than there is settlement to put it against. */
  advanceStranded: boolean;
}

const fmt = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
};

/** Days overdue, or null when it isn't. Ageing is the whole reason to show a due date here. */
const overdueDays = (due?: string | null): number | null => {
  if (!due) return null;
  const d = Math.floor((Date.now() - new Date(due).getTime()) / 86_400_000);
  return d > 0 ? d : null;
};

/**
 * Choose which invoices a payment/receipt voucher settles, on the voucher itself.
 *
 * Before this you paid a supplier on one screen and then went to a different screen to say which
 * of their bills you had just paid. In between, the payment sat as an unapplied credit and the
 * bills still looked outstanding.
 *
 * Shared by AP and AR deliberately: the two sides settle by the same rules, and giving them one
 * component each is how they drift apart.
 *
 * The picks are advisory here — the server re-validates every one of them at posting, because a
 * bill can be settled by someone else between raising the voucher and posting it. What this does
 * is fail fast and visibly, while the operator is still looking at the screen.
 */
export const VoucherAllocationPicker = ({
  partyType, partyId, currencyCode, tenderTotal, value, onChange, readOnly = false,
  canRaiseOnAccount = true, onSummaryChange, saved,
}: Props) => {
  const [items,   setItems]   = useState<OpenItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const noun       = partyType === 'Supplier' ? 'bill' : 'invoice';
  const settleVerb = partyType === 'Supplier' ? 'pay'  : 'collect';

  useEffect(() => {
    if (!partyId) { setItems([]); return; }
    let cancelled = false;

    setLoading(true);
    setLoadErr(null);
    openItemApi.getByParty({ partyType, partyId, currencyCode })
      .then(res => {
        if (cancelled) return;
        // Both sides are kept (ML-07). Debits are what this voucher settles; credits are
        // advances already held for the party, which it can apply as a second source of funds
        // alongside the money it tenders. Bills first, then advances — the operator picks what
        // they are paying before deciding what pays for it.
        const open = (res.data.data ?? []).filter(i => i.outstandingAmount > 0);
        setItems([...open.filter(i => !isCredit(i)), ...open.filter(isCredit)]);
      })
      .catch(() => { if (!cancelled) setLoadErr(`Could not load outstanding ${noun}s.`); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [partyType, partyId, currencyCode, noun]);

  // Changing party or currency invalidates every pick: they belong to the old party's documents.
  // Silently keeping them would send the server ids it will reject at post.
  //
  // A CHANGE, though — not a mount. This section is unmounted whenever the operator moves to
  // another tab, so treating the first run as a change wiped allocations that had just been
  // entered, and wiped a saved voucher's own allocations the moment it was recalled. That was the
  // reported reset (ML-05): the state lives on the form, survives the tab switch, and was then
  // cleared by the picker coming back.
  const seen = useRef<{ partyId: string; currencyCode: string } | null>(null);

  useEffect(() => {
    const previous = seen.current;
    seen.current = { partyId, currencyCode };

    if (previous === null) return;   // first mount — keep whatever the form is already holding
    if (previous.partyId === partyId && previous.currencyCode === currencyCode) return;

    if (value.length > 0) onChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, currencyCode]);

  const picked = useMemo(
    () => new Map(value.map(p => [p.openItemId, p.allocatedAmount])),
    [value],
  );

  // What is being settled, and what is paying for it. An advance sits on the funding side with
  // the voucher's own money, so the two are counted apart (ML-07).
  const byId = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const settling = useMemo(
    () => value.filter(p => !isCredit(byId.get(p.openItemId))).reduce((s, p) => s + p.allocatedAmount, 0),
    [value, byId]);

  const advanceApplied = useMemo(
    () => value.filter(p => isCredit(byId.get(p.openItemId))).reduce((s, p) => s + p.allocatedAmount, 0),
    [value, byId]);

  // "Advance" only when every credit applied really is one; a debit note or return is just a credit.
  const creditNoun = value.every(p => !isCredit(byId.get(p.openItemId)) || isAdvance(byId.get(p.openItemId)))
    ? 'advance' : 'credit';

  // A draft's pick whose document has since been settled elsewhere: it is not in the outstanding
  // list, so it had no row and went to the server unseen, to be refused there.
  const stale = (saved ?? []).filter(a => picked.has(a.openItemId) && !byId.has(a.openItemId));

  const available   = tenderTotal + advanceApplied;
  const unapplied   = available - settling;
  const overApplied = settling > available + 0.01;

  // An advance that pays for nothing would be drawn down with nowhere to go — the same rule the
  // server enforces, surfaced before the operator saves.
  const advanceStranded = advanceApplied > settling + 0.01;

  // Reported rather than recomputed by the parent, so the Save guard and the message under this
  // table can never disagree about whether the voucher is fundable.
  useEffect(() => {
    onSummaryChange?.({ settling, advanceApplied, overApplied, advanceStranded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settling, advanceApplied, overApplied, advanceStranded]);

  const setPick = (item: OpenItemDto, amount: number | null) => {
    const next = value.filter(p => p.openItemId !== item.id);
    if (amount !== null && amount > 0) {
      next.push({ openItemId: item.id, allocatedAmount: Math.min(amount, item.outstandingAmount) });
    }
    onChange(next);
  };

  const toggle = (item: OpenItemDto) => {
    if (picked.has(item.id)) { setPick(item, null); return; }

    if (isCredit(item)) {
      // An advance defaults to covering the shortfall — the reason to reach for one is that the
      // money on the voucher does not cover what is being settled.
      const shortfall = Math.max(0, settling - available);
      setPick(item, Math.min(item.outstandingAmount, shortfall || item.outstandingAmount));
      return;
    }

    // A bill takes whatever the funding can still cover — settling in full is the common case,
    // and putting what is left against the next one is the second.
    const remaining = Math.max(0, available - settling);
    setPick(item, Math.min(item.outstandingAmount, remaining || item.outstandingAmount));
  };

  /** Oldest first until the money runs out — the default any AP clerk would apply by hand. */
  const autoApply = () => {
    // Bills only: "oldest first" is about what gets settled, and reaching for an advance is a
    // decision the operator makes deliberately.
    let left = tenderTotal;
    const next: VoucherAllocationPick[] = [];
    for (const item of items.filter(i => !isCredit(i)).sort(byOldestDue)) {
      if (left <= 0.01) break;
      const amount = Math.min(item.outstandingAmount, left);
      next.push({ openItemId: item.id, allocatedAmount: amount });
      left -= amount;
    }
    onChange(next);
  };

  // Read-only: what this voucher settled, as saved — not what is outstanding today (ADR-003).
  if (readOnly && saved) {
    return <SavedSettlement saved={saved} noun={noun} tenderTotal={tenderTotal} settleVerb={settleVerb} />;
  }

  if (!partyId) {
    return (
      <EmptyNote>
        Select a {partyType.toLowerCase()} to see what they have outstanding.
      </EmptyNote>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading outstanding {noun}s…
      </div>
    );
  }

  if (loadErr) return <EmptyNote tone="error">{loadErr}</EmptyNote>;

  const voucherNoun = partyType === 'Supplier' ? 'payment' : 'receipt';

  if (items.length === 0) {
    return canRaiseOnAccount ? (
      <EmptyNote>
        Nothing outstanding in {currencyCode}. This voucher will post as an on-account {voucherNoun},
        available to allocate later.
      </EmptyNote>
    ) : (
      // Said here rather than on save: there is nothing on this screen the operator can do about
      // it, and finding out after filling in the whole voucher helps nobody (ML-07).
      <EmptyNote tone="error">
        Nothing outstanding in {currencyCode}, and you may not raise a {voucherNoun} that settles
        nothing. Ask an administrator for the &lsquo;{ON_ACCOUNT_PERMISSION[partyType]}&rsquo;
        permission.
      </EmptyNote>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Choose which {noun}s this voucher settles. Anything you leave unapplied stays as an
          on-account credit.
        </p>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="shrink-0"
            leftIcon={<Wand2 className="h-3.5 w-3.5" />}
            onClick={autoApply}
            disabled={tenderTotal <= 0}
            title="Apply to the oldest first until the amount runs out"
          >
            Oldest first
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
            <tr>
              <th className="w-10 px-3 py-2" />
              <th className="px-3 py-2 font-medium">Document</th>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Due</th>
              <th className="px-3 py-2 text-right font-medium">Net</th>
              <th className="px-3 py-2 text-right font-medium">Paid</th>
              <th className="px-3 py-2 text-right font-medium">Due</th>
              <th className="px-3 py-2 text-right font-medium">Settling</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map(item => {
              const amount = picked.get(item.id);
              const isOn   = amount !== undefined;
              const late   = overdueDays(item.dueDate);
              const credit = isCredit(item);

              return (
                <tr
                  key={item.id}
                  className={cn('align-top', isOn && 'bg-emerald-50/40 dark:bg-emerald-900/10')}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={isOn}
                      disabled={readOnly}
                      onChange={() => toggle(item)}
                      aria-label={`${credit ? 'Apply' : 'Settle'} ${item.documentNumber}`}
                      className="h-4 w-4 rounded border-slate-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs text-slate-800 dark:text-slate-200">
                        {item.documentNumber}
                      </span>
                      {credit && (
                        <span className="rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-medium
                                         text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          {CREDIT_LABEL[item.documentType] ?? 'Credit'}
                        </span>
                      )}
                    </div>
                    {/* The counterparty's own number and the source refs — the numbers a human
                        actually recognises when reconciling against a supplier statement. */}
                    <DocumentReferences item={item} showSourceNumber />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                    {fmtDate(item.documentDate)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="text-slate-600 dark:text-slate-400">{fmtDate(item.dueDate)}</span>
                    {late && (
                      <span className="ml-1.5 rounded bg-rose-100 px-1 py-0.5 text-[10px] font-medium text-rose-700
                                       dark:bg-rose-900/40 dark:text-rose-300">
                        {late}d
                      </span>
                    )}
                  </td>
                  {/* Net, paid and due rather than the balance alone (ML-06): a partly paid bill
                      and an untouched one can show the same balance, and which it is decides
                      whether the operator is chasing a shortfall or a whole invoice. */}
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-500 dark:text-slate-400">
                    {credit ? `-${fmt(item.originalAmount)}` : fmt(item.originalAmount)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-500 dark:text-slate-400">
                    {item.allocatedAmount > 0.005 ? fmt(item.allocatedAmount) : '—'}
                  </td>
                  <td className={cn(
                    'px-3 py-2 text-right font-mono text-xs font-medium',
                    credit
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-slate-700 dark:text-slate-300',
                  )}>
                    {/* A credit is a balance owed the other way, so it reads negative (ML-07). */}
                    {credit ? `-${fmt(item.outstandingAmount)}` : fmt(item.outstandingAmount)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isOn ? (
                      <Input
                        type="number"
                        size="xs"
                        step="0.01"
                        // A credit is shown negative, as it reduces what is paid; the pick stays positive.
                        min={credit ? -item.outstandingAmount : 0}
                        max={credit ? 0 : item.outstandingAmount}
                        value={credit ? -(amount ?? 0) : amount}
                        disabled={readOnly}
                        aria-label={`Amount settling ${item.documentNumber}`}
                        onChange={e => {
                          const v = e.target.value === '' ? 0 : Math.abs(Number(e.target.value));
                          setPick(item, Number.isFinite(v) ? v : 0);
                        }}
                        fieldClassName="w-28 ml-auto"
                        className="text-right font-mono"
                      />
                    ) : (
                      <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {stale.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800
                        dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-300">
          <p className="mb-2 flex items-center gap-1.5 font-medium">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            No longer outstanding — settled elsewhere since this voucher was saved. Remove them before saving.
          </p>
          <ul className="space-y-1">
            {stale.map(a => (
              <li key={a.openItemId} className="flex items-center justify-between gap-3">
                <span className="font-mono">{a.documentNumber}</span>
                <span className="font-mono">{fmt(picked.get(a.openItemId) ?? a.allocatedAmount)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  leftIcon={<X className="h-3 w-3" />}
                  onClick={() => onChange(value.filter(p => p.openItemId !== a.openItemId))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The three numbers that decide whether this voucher is right. */}
      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-xs">
        <Total label={`Amount to ${settleVerb}`} value={tenderTotal} />
        {advanceApplied > 0.01 && <Total label={creditNoun === 'advance' ? 'Advance applied' : 'Credits applied'} value={-advanceApplied} />}
        <Total label="Settling" value={settling} />
        <Total
          label={unapplied < 0 ? 'Over-applied' : 'On account'}
          value={Math.abs(unapplied)}
          tone={overApplied ? 'error' : unapplied > 0.01 ? 'warn' : 'ok'}
        />
      </div>

      {overApplied && (
        <p className="flex items-start gap-1.5 text-xs text-rose-600 dark:text-rose-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          This voucher settles {fmt(settling)} but only has {fmt(available)} to settle with
          {advanceApplied > 0.01 && ` (${fmt(tenderTotal)} paid plus ${fmt(advanceApplied)} in ${creditNoun}s)`}.
        </p>
      )}

      {!canRaiseOnAccount && settling <= 0.01 && (
        <p className="flex items-start gap-1.5 text-xs text-rose-600 dark:text-rose-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Choose at least one {noun} to settle. You may not raise a {voucherNoun} on account —
          that needs the &lsquo;{ON_ACCOUNT_PERMISSION[partyType]}&rsquo; permission.
        </p>
      )}

      {advanceStranded && (
        <p className="flex items-start gap-1.5 text-xs text-rose-600 dark:text-rose-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          This voucher applies {fmt(advanceApplied)} in {creditNoun}s but only settles {fmt(settling)}.
          A {creditNoun} has to go somewhere — settle more, or apply less of it.
        </p>
      )}
    </div>
  );
};

/**
 * A submitted, approved or posted voucher's settlement, from what it saved (ADR-003). The amount
 * shown is what THIS voucher settled; today's balance of the document is a different question.
 */
const SavedSettlement = ({ saved, noun, tenderTotal, settleVerb }: {
  saved: SavedVoucherAllocation[]; noun: string; tenderTotal: number; settleVerb: string;
}) => {
  if (saved.length === 0) {
    return <EmptyNote>This voucher settles no {noun}s — it was raised on account.</EmptyNote>;
  }
  const total = saved.reduce((sum, a) => sum + a.allocatedAmount, 0);
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 dark:text-slate-400">The {noun}s this voucher settled.</p>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">Document</th>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Due</th>
              <th className="px-3 py-2 text-right font-medium">Settled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {saved.map(a => (
              <tr key={a.openItemId}>
                <td className="px-3 py-2 font-mono text-xs text-slate-800 dark:text-slate-200">{a.documentNumber}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{fmtDate(a.documentDate)}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{fmtDate(a.dueDate)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs font-medium text-slate-700 dark:text-slate-300">
                  {fmt(a.allocatedAmount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-xs">
        <Total label={`Amount to ${settleVerb}`} value={tenderTotal} />
        <Total label="Allocated" value={total} />
      </div>
    </div>
  );
};

// ── Bits ─────────────────────────────────────────────────────────────────────

/**
 * A credit is money already held for this party — an advance, or a note in their favour. It funds a
 * settlement rather than being settled (ML-07), and its outstanding balance is shown negative
 * because that is what it is: a balance owed the other way.
 */
/** Named in the refusal so an administrator knows what to grant. */
const ON_ACCOUNT_PERMISSION: Record<'Supplier' | 'Customer', string> = {
  Supplier: 'RAISE_ON_ACCOUNT_AP_PAYMENT',
  Customer: 'RAISE_ON_ACCOUNT_AR_RECEIPT',
};

const isCredit = isCreditItem;

const isAdvance = (i: OpenItemDto | undefined) =>
  !!i && ['AdvancePayment', 'AdvanceReceipt'].includes(i.documentType);

/** What each credit is, in the operator's words. ApCreditNote is the debit note we raise (ADR-015). */
const CREDIT_LABEL: Record<string, string> = {
  AdvancePayment:  'Advance',
  AdvanceReceipt:  'Advance',
  ApCreditNote:    'Debit note',
  ArCreditNote:    'Credit note',
  SupplierPayment: 'On account',
  CustomerReceipt: 'On account',
};

const byOldestDue = (a: OpenItemDto, b: OpenItemDto) =>
  new Date(a.dueDate ?? a.documentDate).getTime() - new Date(b.dueDate ?? b.documentDate).getTime();

const Total = ({ label, value, tone = 'ok' }: { label: string; value: number; tone?: 'ok' | 'warn' | 'error' }) => (
  <div className="flex items-center gap-2">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className={cn(
      'font-mono font-medium tabular-nums',
      tone === 'error' ? 'text-rose-600 dark:text-rose-400'
        : tone === 'warn' ? 'text-amber-600 dark:text-amber-400'
        : 'text-slate-800 dark:text-slate-100',
    )}>
      {fmt(value)}
    </span>
  </div>
);

const EmptyNote = ({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'error' }) => (
  <p className={cn(
    'rounded-lg border border-dashed p-6 text-center text-sm',
    tone === 'error'
      ? 'border-rose-200 text-rose-600 dark:border-rose-800 dark:text-rose-400'
      : 'border-slate-200 text-slate-500 dark:border-slate-700 dark:text-slate-400',
  )}>
    {children}
  </p>
);
